import React, { useEffect, useRef, useState } from "react";
import { G, FONT_FAMILY, OVERVIEW } from "../../constants/theme";

// Sticky in-page navigator for the Overview page. Sits directly under the top
// navigation; each pill jumps to a section anchor and the active pill tracks
// the section currently in view. Navy chrome to read as one unit with TopNav.
//
// props.sections: [{ id, label }]  — ids must match the section wrapper ids.
// props.topOffset: px the sticky bar sits below (defaults to the 56px TopNav).

export default function SectionNav({ sections = [], topOffset = 56 }) {
  const [active, setActive] = useState(sections[0]?.id || null);
  const railRef = useRef(null);

  // Scroll-spy — the first section (in declared order) inside the active band.
  useEffect(() => {
    const els = sections
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);
    if (!els.length) return;

    const visible = new Set();
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) visible.add(e.target.id);
          else visible.delete(e.target.id);
        }
        const first = sections.find((s) => visible.has(s.id));
        if (first) setActive(first.id);
      },
      { rootMargin: `-${topOffset + 64}px 0px -55% 0px`, threshold: [0, 0.15, 0.5, 1] }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [sections, topOffset]);

  // Keep the active pill scrolled into view within the (scrollable) rail.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || !active) return;
    const btn = rail.querySelector(`[data-nav="${active}"]`);
    if (btn) {
      const bL = btn.offsetLeft;
      const bR = bL + btn.offsetWidth;
      if (bL < rail.scrollLeft) rail.scrollTo({ left: bL - 16, behavior: "smooth" });
      else if (bR > rail.scrollLeft + rail.clientWidth)
        rail.scrollTo({ left: bR - rail.clientWidth + 16, behavior: "smooth" });
    }
  }, [active]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    setActive(id); // optimistic
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="no-print"
      style={{
        position: "sticky",
        top: topOffset,
        zIndex: 90,
        background: G.dark,
        borderBottom: `1px solid ${G.darkBorder}`,
        boxShadow: "0 10px 20px -14px rgba(0,0,0,0.55)",
      }}
    >
      <div
        style={{
          maxWidth: OVERVIEW.contentMax,
          margin: "0 auto",
          padding: `0 ${OVERVIEW.gutter}px`,
          boxSizing: "border-box",
        }}
      >
        <div
          ref={railRef}
          className="themed-scroll-dark"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: 52,
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none",
          }}
        >
          <span
            style={{
              ...OVERVIEW.eyebrow,
              color: "rgba(255,255,255,0.32)",
              flexShrink: 0,
            }}
          >
            Navigate to
          </span>
          <span
            aria-hidden="true"
            style={{
              width: 1,
              height: 18,
              background: "rgba(255,255,255,0.14)",
              flexShrink: 0,
              marginRight: 4,
            }}
          />

          {sections.map((s) => {
            const on = s.id === active;
            return (
              <button
                key={s.id}
                type="button"
                data-nav={s.id}
                onClick={() => go(s.id)}
                aria-current={on ? "true" : undefined}
                style={{
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  appearance: "none",
                  border: `1px solid ${on ? G.brandHover : "rgba(255,255,255,0.09)"}`,
                  borderRadius: 7,
                  padding: "7px 13px",
                  fontSize: 12,
                  fontWeight: on ? 700 : 600,
                  fontFamily: FONT_FAMILY,
                  letterSpacing: "0.01em",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  background: on ? G.brand : "transparent",
                  color: on ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                  boxShadow: on ? "0 2px 10px -3px rgba(37,99,235,0.6)" : "none",
                  transform: "translateY(0)",
                  transition: "background 0.16s, color 0.16s, border-color 0.16s, transform 0.16s",
                }}
                onMouseEnter={(e) => {
                  if (!on) {
                    e.currentTarget.style.background = "rgba(255,255,255,0.07)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.95)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!on) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.09)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.55)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: on ? "#FFFFFF" : "rgba(255,255,255,0.25)",
                    transition: "background 0.16s",
                  }}
                />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
